/**
 * DviStageEvidence - Stage 4: Evidence packet builder
 * 
 * Build and view evidence packets for analyzed groups.
 */

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  FileCheck, 
  Loader2, 
  Eye,
  Camera,
  MessageSquare,
  Info
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import type { FindingGroup } from '@/hooks/useDviFindingGroups';

interface EvidencePacket {
  finding_group: {
    id: string;
    title: string;
    condition: string;
    confidence: number;
  };
  explanation?: {
    summary: string;
    confidence_breakdown: Record<string, number>;
  };
  original_photos: string[];
  decision_history: Array<{
    action: string;
    occurred_at: string;
    actor_type: string;
  }>;
  applied_jobs: Array<{
    catalog_job_id: string;
    notes: string;
  }>;
}

interface DviStageEvidenceProps {
  analyzedGroups: FindingGroup[];
  canBuildEvidence: boolean;
  isLoading: boolean;
}

export function DviStageEvidence({
  analyzedGroups,
  canBuildEvidence,
  isLoading,
}: DviStageEvidenceProps) {
  const { toast } = useToast();
  const [buildingGroupId, setBuildingGroupId] = useState<string | null>(null);
  const [packets, setPackets] = useState<Map<string, EvidencePacket>>(new Map());
  const [expandedGroupId, setExpandedGroupId] = useState<string | null>(null);

  const buildPacket = async (groupId: string) => {
    setBuildingGroupId(groupId);
    try {
      const { data, error } = await supabase.functions.invoke('dvi-build-evidence-packet', {
        body: { finding_group_id: groupId },
      });

      if (error) throw error;
      
      setPackets((prev) => {
        const next = new Map(prev);
        next.set(groupId, data);
        return next;
      });
      setExpandedGroupId(groupId);
      toast({ title: 'Evidence packet built' });
    } catch (error) {
      toast({
        title: 'Failed to build evidence packet',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setBuildingGroupId(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (analyzedGroups.length === 0) {
    return (
      <div className="text-center py-8 space-y-2">
        <FileCheck className="h-12 w-12 text-muted-foreground mx-auto" />
        <p className="text-sm text-muted-foreground">No analyzed groups</p>
        <p className="text-xs text-muted-foreground">
          Analyze finding groups first to build evidence packets
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Info */}
      <div className="bg-muted/50 rounded p-2 flex items-start gap-2">
        <Info className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
        <p className="text-xs text-muted-foreground">
          Evidence packets compile photos, AI explanations, and decision history for compliance and customer communication.
        </p>
      </div>

      {/* Groups with evidence status */}
      <div className="space-y-2">
        {analyzedGroups.map((group) => {
          const packet = packets.get(group.id);
          const isBuilding = buildingGroupId === group.id;
          const isExpanded = expandedGroupId === group.id;

          return (
            <Card key={group.id}>
              <CardContent className="p-3 space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <FileCheck className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium truncate">
                      {group.title || 'Untitled Group'}
                    </span>
                    {packet && (
                      <Badge variant="outline" className="bg-green-500/10 text-green-600 text-xs">
                        Built
                      </Badge>
                    )}
                  </div>
                  
                  {canBuildEvidence && (
                    <div className="flex items-center gap-1.5 shrink-0">
                      {packet && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setExpandedGroupId(isExpanded ? null : group.id)}
                          className="gap-1.5 h-7"
                        >
                          <Eye className="h-3 w-3" />
                          {isExpanded ? 'Hide' : 'View'}
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant={packet ? 'ghost' : 'secondary'}
                        onClick={() => buildPacket(group.id)}
                        disabled={isBuilding}
                        className="gap-1.5 h-7"
                      >
                        {isBuilding ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <FileCheck className="h-3 w-3" />
                        )}
                        {packet ? 'Rebuild' : 'Build'}
                      </Button>
                    </div>
                  )}
                </div>

                {/* Expanded packet view */}
                {isExpanded && packet && (
                  <div className="mt-2 pt-2 border-t space-y-3">
                    {/* Photos */}
                    {packet.original_photos.length > 0 && (
                      <div>
                        <p className="text-xs font-medium text-muted-foreground mb-1 flex items-center gap-1">
                          <Camera className="h-3 w-3" />
                          Photos ({packet.original_photos.length})
                        </p>
                        <div className="flex gap-1 overflow-x-auto">
                          {packet.original_photos.slice(0, 4).map((url, i) => (
                            <img 
                              key={i}
                              src={url} 
                              alt={`Evidence ${i + 1}`}
                              className="h-16 w-16 object-cover rounded shrink-0"
                            />
                          ))}
                          {packet.original_photos.length > 4 && (
                            <div className="h-16 w-16 bg-muted rounded flex items-center justify-center shrink-0">
                              <span className="text-xs text-muted-foreground">
                                +{packet.original_photos.length - 4}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Explanation summary */}
                    {packet.explanation?.summary && (
                      <div>
                        <p className="text-xs font-medium text-muted-foreground mb-1 flex items-center gap-1">
                          <MessageSquare className="h-3 w-3" />
                          AI Explanation
                        </p>
                        <p className="text-xs bg-muted/50 rounded p-2">
                          {packet.explanation.summary}
                        </p>
                      </div>
                    )}

                    {/* Decision history */}
                    {packet.decision_history.length > 0 && (
                      <div>
                        <p className="text-xs font-medium text-muted-foreground mb-1">
                          Decision History
                        </p>
                        <ul className="text-xs space-y-0.5">
                          {packet.decision_history.slice(0, 3).map((entry, i) => (
                            <li key={i} className="text-muted-foreground">
                              • {entry.action} by {entry.actor_type} on{' '}
                              {new Date(entry.occurred_at).toLocaleDateString()}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
